import {
  AppBar,
  Box,
  Button,
  Container,
  Grid,
  Modal,
  Paper,
  Stack,
  TextField,
  Toolbar,
  Typography,
} from "@mui/material";
import axios from "axios";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import OrangeCardIcon from "./components/icons/OrangeCardIcon";
import OrangeClockIcon from "./components/icons/OrangeClockIcon";
import OrangeDocumentIcon from "./components/icons/OrangeDocumentIcon";
import OrangeHomeIcon from "./components/icons/OrangeHomeIcon";
import Register from "./Register";

// http://127.0.0.1:5000/login for testing locally
// https://blooming-hamlet-96520-b99bb79f6f22.herokuapp.com/login for hosting online
const backendUrl = process.env.REACT_APP_API_URL;

const stackData = [
  {
    icon: <OrangeClockIcon />,
    title: "Save Hours of Manual Work",
    description:
      "No more fixing spreadsheets by hand—clean your lists instantly.",
  },
  {
    icon: <OrangeCardIcon />,
    title: "Reduce Marketing Costs",
    description:
      "Remove unnecessary data so you’re not wasting money on extra mailers.",
  },
  {
    icon: <OrangeDocumentIcon />,
    title: "Get Clean, Ready-to-Use Lists",
    description:
      "Upload your data, clean it, and download a formatted version in seconds.",
  },
  {
    icon: <OrangeHomeIcon />,
    title: "Made for Real Estate Professionals",
    description:
      "Built for investors and agents who need accurate marketing lists.",
  },
];

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [authMessage, setAuthMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  // Get API URL from .env

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      setAuthMessage("Please fill in all fields.");
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(`${backendUrl}/login`, {
        email,
        password,
      });

      if (response.status === 200) {
        const token = response.data.access_token;
        const userId = response.data.user_id;
        localStorage.setItem("token", token); // Store token
        localStorage.setItem("userId", userId); // Store user_id

        navigate("/upload"); // Redirect to upload page
      } else {
        setAuthMessage("Login failed.");
      }
    } catch (error) {
      setAuthMessage("Invalid email or password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <AppBar
        position="static"
        color="transparent"
        sx={{
          boxShadow: "none",
          position: "absolute",
          top: 0,
          width: "100%",
        }}
      >
        <Container maxWidth="xl" sx={{ px: 3 }}>
          <Toolbar style={{ minHeight: 90, padding: 0, width: "100%" }}>
            <Link to={"/upload"}>
              <img src="/logo-icon.svg" alt="logo" width={131} height={30} />
            </Link>

            <Box ml={"auto"} display={{ xs: "none", md: "flex" }} gap={2}>
              <Link to={"/how-it-works"}>
                <Button
                  variant="text"
                  color="default"
                  disableElevation
                  sx={{ px: 2 }}
                >
                  How it works
                </Button>
              </Link>
              <Link to={"/pricing"}>
                <Button
                  variant="text"
                  color="default"
                  disableElevation
                  sx={{ px: 2 }}
                >
                  Pricing
                </Button>
              </Link>
              <Button
                onClick={handleOpen}
                variant="contained"
                color="brand"
                disableElevation
                sx={{ px: 4 }}
              >
                Login
              </Button>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>
      <Grid container sx={{ minHeight: "100vh" }}>
        {/* Left Side - Branding & Info */}
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            background: "linear-gradient(18deg, #F9A252 1.15%, #F0F2F4 62.36%)",
            minHeight: "100%",
          }}
        >
          <Container
            maxWidth="lg"
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "100%",
            }}
          >
            <Box
              sx={{
                maxWidth: "447px",
                width: "100%",
                minHeight: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                gap: 4,
                pt: { xs: "120px", md: "30px" },
                pb: "30px",
              }}
            >
              <Box>
                <Typography variant="h2">Real Estate List Cleaner</Typography>
                <Typography variant="body1" color="#8B8B8B" mt={1.5}>
                  Instantly clean and format your real estate marketing lists.
                </Typography>

                <Stack spacing={2} sx={{ mt: "40px" }}>
                  {stackData.map((item, index) => (
                    <Paper
                      key={index}
                      elevation={0}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        padding: "24px 30px",
                        borderRadius: "8px",
                        boxShadow:
                          "3px 1px 6px 0px rgba(184, 184, 184, 0.1),11px 4px 11px 0px rgba(184, 184, 184, 0.09),24px 9px 15px 0px rgba(184, 184, 184, 0.05),42px 16px 18px 0px rgba(184, 184, 184, 0.01),66px 25px 20px 0px rgba(184, 184, 184, 0)",
                        "& svg": {
                          filter:
                            "drop-shadow(0px 4px 10px rgba(0, 0, 0, 0.1))",
                          flexShrink: 0,
                        },
                      }}
                    >
                      {item.icon}
                      <Box display={"flex"} flexDirection={"column"} gap={1}>
                        <Typography variant="body3" color="#333333">
                          {item.title}
                        </Typography>
                        <Typography
                          variant="body6"
                          color="#8B8B8B"
                          maxWidth={"280px"}
                        >
                          {item.description}
                        </Typography>
                      </Box>
                    </Paper>
                  ))}
                </Stack>
              </Box>
            </Box>
          </Container>
        </Grid>

        {/* Right Side - Login Form */}
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Container maxWidth="lg">
            <Box maxWidth={"400px"} mx="auto" py={8}>
              <Typography variant="h2" textAlign={"center"}>
                Sign in
              </Typography>
              <Typography
                variant="body4"
                textAlign={"center"}
                color="#8B8B8B"
                mt={1.5}
              >
                Please login to continue to your account.
              </Typography>
              <Box mt={2}>
                <form onSubmit={handleLogin}>
                  <TextField
                    type="email"
                    label="Email"
                    autoComplete="username"
                    variant="outlined"
                    fullWidth
                    margin="normal"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                  <TextField
                    type="password"
                    label="Password"
                    autoComplete="current-password"
                    variant="outlined"
                    fullWidth
                    margin="normal"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />

                  <Button
                    type="submit"
                    variant="contained"
                    color="default"
                    size="large"
                    fullWidth
                    sx={{
                      mt: 2,
                    }}
                    disabled={loading}
                  >
                    {loading ? "Signing in..." : "Sign in"}
                  </Button>
                </form>
              </Box>

              {authMessage && (
                <Typography color="error" sx={{ mt: 2 }}>
                  {authMessage}
                </Typography>
              )}

              {/* Removed "or" divider and third-party sign-in buttons */}

              <Typography
                variant="body1"
                textAlign={"center"}
                mt={"32px"}
                color="#6C6C6C"
              >
                Don't have an account?{" "}
                <Typography
                  color="brand"
                  component={"span"}
                  sx={{ cursor: "pointer", textDecoration: "underline" }}
                  onClick={handleOpen}
                >
                  Sign up
                </Typography>
              </Typography>

              <Modal
                open={open}
                onClose={handleClose}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
                sx={{
                  display: "flex",
                  alignItems: { xs: "start", md: "center" },
                  justifyContent: "center",
                  py: 6,
                  overflow: "auto",
                }}
              >
                <Box>
                  <Register onClose={handleClose} />
                </Box>
              </Modal>
            </Box>
          </Container>
        </Grid>
      </Grid>
    </>
  );
};

export default Login;
